import { useMemo } from 'react';
import { useAppSelector } from '../../../../../redux/hooks';
import { isToolsPage } from '../../../../../utils/routing/route-utils';
import type { DisplayJob } from '../../../../../types';
import type { ApplicationState } from '../../../../../redux/features/jobs/jobsSlice';

export interface JobState {
  jobId: string | undefined;
  isSaved: boolean;
  isApplied: boolean;
  isHidden: boolean;
  currentApplicationState: ApplicationState | null;
  isToolsPage: boolean;
}

export interface UseJobStateProps {
  displayJob: DisplayJob | null | undefined;
}

/** Normalize job id to string so comparisons with Redux (which stores string ids) are reliable. */
function normalizeJobId(value: string | number | undefined): string | undefined {
  if (value === undefined || value === null) return undefined;
  const s = String(value);
  return s === '' ? undefined : s;
}

/**
 * Hook to get the current state of a job (saved, applied, hidden, applicationState)
 */
export function useJobState({ displayJob }: UseJobStateProps): JobState {
  const rawId = displayJob?.objectID;
  const jobId = normalizeJobId(rawId as string | number | undefined);

  const isSaved = useAppSelector((state: any) => {
    if (!jobId) return false;
    return state.jobs.savedJobs.some((j: any) => String(j.id) === jobId);
  });

  const isApplied = useAppSelector((state: any) => {
    if (!jobId) return false;
    return state.jobs.appliedJobs.some((j: any) => String(j.id) === jobId);
  });

  const currentApplicationState = useAppSelector((state: any) => {
    if (!jobId) return null;
    const allJobs = [
      ...state.jobs.viewedJobs,
      ...state.jobs.savedJobs,
      ...state.jobs.appliedJobs,
    ];
    const job = allJobs.find((j: any) => String(j.id) === jobId);
    return (job?.applicationState ?? null) as ApplicationState | null;
  }) as ApplicationState | null;

  const isHidden = useAppSelector((state: any) => {
    if (!jobId) return false;
    const allJobs = [
      ...state.jobs.viewedJobs,
      ...state.jobs.savedJobs,
      ...state.jobs.appliedJobs,
    ];
    const job = allJobs.find((j: any) => String(j.id) === jobId);
    return job?.isHidden ?? false;
  });

  // Check if this is a tools page
  const isToolsPageValue = useMemo(() => {
    if (typeof window === 'undefined') return false;
    try {
      const pathname = window.location.pathname;
      return isToolsPage(pathname);
    } catch {
      return false;
    }
  }, []);

  return {
    jobId,
    isSaved,
    isApplied,
    isHidden,
    currentApplicationState,
    isToolsPage: isToolsPageValue,
  };
}
